import React, { useState, useMemo } from 'react';
import { digitsOnly, MoneyField } from './persianMoney.jsx';
import { X, MapPin, ChevronLeft, ChevronRight, Pencil, Zap, Send } from 'lucide-react';
import LocationPicker from './locationPicker.jsx';
import { AreaPreview } from './areaMap.jsx';
import './requestWizard.scss';

const DEAL_TYPES = [
  { id: 'sale', label: 'خرید' },
  { id: 'rent', label: 'اجاره' },
];

const PROPERTY_TYPES = [
  { id: 'apartment', label: 'آپارتمان' },
  { id: 'villa', label: 'ویلایی' },
  { id: 'land', label: 'زمین' },
  { id: 'office', label: 'اداری' },
  { id: 'shop', label: 'مغازه' },
];

const ROOM_OPTIONS = ['۱', '۲', '۳', '۴', '۵+'];

const AMENITIES = [
  { id: 'elevator', label: 'آسانسور' },
  { id: 'parking', label: 'پارکینگ' },
  { id: 'storage', label: 'انباری' },
  { id: 'balcony', label: 'بالکن' },
];

const AMENITY_LABELS = Object.fromEntries(AMENITIES.map((a) => [a.id, a.label]));
const PROPERTY_LABELS = Object.fromEntries(PROPERTY_TYPES.map((p) => [p.id, p.label]));

export const STEPS = [
  { id: 'dealType', title: 'نوع معامله', hasNote: false },
  { id: 'propertyType', title: 'نوع ملک', hasNote: false },
  { id: 'price', title: 'بودجه', hasNote: true },
  { id: 'area', title: 'متراژ', hasNote: true },
  { id: 'rooms', title: 'تعداد اتاق', hasNote: true },
  { id: 'buildYear', title: 'سن بنا', hasNote: true },
  { id: 'amenities', title: 'امکانات', hasNote: true },
  { id: 'location', title: 'محدوده جغرافیایی', hasNote: false },
];

// متن‌های پیشنهادیِ هر مرحله: کاربر با زدنِ هر کدام، آن را به توضیحاتِ همان مرحله اضافه می‌کند (قابل ویرایش)
const NOTE_SUGGESTIONS = {
  price: [
    'واقعاً بیشتر از این بودجه نمی‌تونم پرداخت کنم',
    'اگر خونه خیلی تاپ باشه، می‌تونم یه خورده بیشتر پرداخت کنم',
  ],
  area: ['حداقل متراژ مدنظرم ۹۰ متره، ولی اگر خونه لوکس باشه با ۷۰ متر هم کنار میام'],
  rooms: ['اگر خونه خیلی خوب باشه، شاید دو خواب هم مشکلی نداشته باشم'],
  buildYear: ['اگر نما و داخل بازسازی شده باشه، مشکلی با سن بنا ندارم'],
  amenities: [
    'اگر طبقه اول یا همکف باشه، شاید مشکلی با نداشتن آسانسور نداشته باشم',
    'اگر خونه خوبی باشه، پارکینگ نداشتنش عیب نیست',
  ],
};

const initialForm = {
  dealType: 'sale',
  propertyType: 'apartment',
  priceFrom: '',
  priceTo: '',
  depositFrom: '',
  depositTo: '',
  rentFrom: '',
  rentTo: '',
  areaFrom: '',
  areaTo: '',
  rooms: [],
  buildYearFrom: '',
  buildYearTo: '',
  amenities: [],
  location: null,
};

function toggleArrayItem(arr, item) {
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
}

function formatRange(from, to, unit) {
  if (!from && !to) return 'انتخاب نشده';
  if (from && to) return `${from} تا ${to} ${unit}`;
  if (from) return `از ${from} ${unit}`;
  return `تا ${to} ${unit}`;
}

export function getStepSummary(stepId, form) {
  switch (stepId) {
    case 'dealType':
      return DEAL_TYPES.find((d) => d.id === form.dealType)?.label || '—';
    case 'propertyType':
      return PROPERTY_LABELS[form.propertyType] || '—';
    case 'price':
      if (form.dealType === 'rent') {
        const deposit = formatRange(form.depositFrom, form.depositTo, 'تومان');
        const rent = formatRange(form.rentFrom, form.rentTo, 'تومان');
        return `ودیعه: ${deposit} — اجاره: ${rent}`;
      }
      return formatRange(form.priceFrom, form.priceTo, 'تومان');
    case 'area':
      return formatRange(form.areaFrom, form.areaTo, 'متر');
    case 'rooms':
      return form.rooms.length ? form.rooms.join('، ') : 'انتخاب نشده';
    case 'buildYear':
      return formatRange(form.buildYearFrom, form.buildYearTo, 'سال');
    case 'amenities':
      return form.amenities.length
        ? form.amenities.map((id) => AMENITY_LABELS[id]).join('، ')
        : 'انتخاب نشده';
    case 'location':
      return form.location ? 'محدوده روی نقشه رسم شد' : 'انتخاب نشده';
    default:
      return '—';
  }
}

// STAGE_STEPS -> در حال پرکردن فیلترها
// STAGE_REVIEW -> مرور نهایی
// STAGE_SEND -> انتخاب نحوه ارسال
const STAGE_STEPS = 'steps';
const STAGE_REVIEW = 'review';
const STAGE_SEND = 'send';

function RequestWizard({ onClose, onSubmit }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [notes, setNotes] = useState({});
  const [stage, setStage] = useState(STAGE_STEPS);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [sendMethod, setSendMethod] = useState('paid'); // 'free' | 'paid' — دیفالت غیررایگان

  // عدد رندوم مشاورین داخل‌اپلیکیشنی؛ فقط یک‌بار در طول عمر کامپوننت ساخته می‌شود
 const freeConsultantsCount = useMemo(
  () => Math.floor(Math.random() * (560 - 480 + 1)) + 480,
  []
);

const paidConsultantsCount = useMemo(
  () => Math.floor(Math.random() * (1500 - 700 + 1)) + 700,
  []
);

  const step = STEPS[stepIndex];
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === STEPS.length - 1;

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const updateNote = (stepId, value) => setNotes((prev) => ({ ...prev, [stepId]: value }));

const goNext = () => {
  if (stage === STAGE_SEND) {
    const targetCount = sendMethod === 'free' ? freeConsultantsCount : paidConsultantsCount;
    onSubmit?.({ form, notes, sendMethod, targetCount });
    return;
  }
  if (stage === STAGE_REVIEW) {
    setStage(STAGE_SEND);
    return;
  }
  if (isLastStep) {
    setStage(STAGE_REVIEW);
    return;
  }
  setStepIndex((i) => i + 1);
};

  const goBack = () => {
    if (stage === STAGE_SEND) {
      setStage(STAGE_REVIEW);
      return;
    }
    if (stage === STAGE_REVIEW) {
      setStage(STAGE_STEPS);
      return;
    }
    if (isFirstStep) {
      onClose?.();
      return;
    }
    setStepIndex((i) => i - 1);
  };

  const handleEditStep = (index) => {
    setStepIndex(index);
    setStage(STAGE_STEPS);
  };

  const renderStepContent = () => {
    switch (step.id) {
      case 'dealType':
        return (
          <div className="request-wizard__segment">
            {DEAL_TYPES.map((d) => (
              <button
                key={d.id}
                type="button"
                className={form.dealType === d.id ? 'is-active' : ''}
                onClick={() => update('dealType', d.id)}
              >
                {d.label}
              </button>
            ))}
          </div>
        );

      case 'propertyType':
        return (
          <div className="request-wizard__chips">
            {PROPERTY_TYPES.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`request-wizard__chip ${form.propertyType === p.id ? 'is-selected' : ''}`}
                onClick={() => update('propertyType', p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
        );

      case 'price':
        if (form.dealType === 'rent') {
          return (
            <div className="request-wizard__range-group">
              <div className="request-wizard__field">
                <span className="request-wizard__mini-label">ودیعه (تومان)</span>
                <div className="request-wizard__range">
                  <MoneyField placeholder="از" value={form.depositFrom} onChange={(v) => update('depositFrom', v)} />
                  <span>تا</span>
                  <MoneyField placeholder="تا" value={form.depositTo} onChange={(v) => update('depositTo', v)} />
                </div>
              </div>
              <div className="request-wizard__field">
                <span className="request-wizard__mini-label">اجاره ماهیانه (تومان)</span>
                <div className="request-wizard__range">
                  <MoneyField placeholder="از" value={form.rentFrom} onChange={(v) => update('rentFrom', v)} />
                  <span>تا</span>
                  <MoneyField placeholder="تا" value={form.rentTo} onChange={(v) => update('rentTo', v)} />
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="request-wizard__range">
            <MoneyField placeholder="از (تومان)" value={form.priceFrom} onChange={(v) => update('priceFrom', v)} />
            <span>تا</span>
            <MoneyField placeholder="تا (تومان)" value={form.priceTo} onChange={(v) => update('priceTo', v)} />
          </div>
        );

      case 'area':
        return (
          <div className="request-wizard__range">
            <input
              type="text"
              inputMode="numeric"
              placeholder="از (متر)"
              value={form.areaFrom}
              onChange={(e) => update('areaFrom', digitsOnly(e.target.value))}
            />
            <span>تا</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="تا (متر)"
              value={form.areaTo}
              onChange={(e) => update('areaTo', digitsOnly(e.target.value))}
            />
          </div>
        );

      case 'rooms':
        return (
          <div className="request-wizard__chips">
            {ROOM_OPTIONS.map((r) => (
              <button
                key={r}
                type="button"
                className={`request-wizard__chip ${form.rooms.includes(r) ? 'is-selected' : ''}`}
                onClick={() => update('rooms', toggleArrayItem(form.rooms, r))}
              >
                {r}
              </button>
            ))}
          </div>
        );

      case 'buildYear':
        return (
          <div className="request-wizard__range">
            <input
              type="text"
              inputMode="numeric"
              placeholder="از (سال)"
              value={form.buildYearFrom}
              onChange={(e) => update('buildYearFrom', digitsOnly(e.target.value))}
            />
            <span>تا</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="تا (سال)"
              value={form.buildYearTo}
              onChange={(e) => update('buildYearTo', digitsOnly(e.target.value))}
            />
          </div>
        );

      case 'amenities':
        return (
          <div className="request-wizard__chips">
            {AMENITIES.map((a) => (
              <button
                key={a.id}
                type="button"
                className={`request-wizard__chip ${form.amenities.includes(a.id) ? 'is-selected' : ''}`}
                onClick={() => update('amenities', toggleArrayItem(form.amenities, a.id))}
              >
                {a.label}
              </button>
            ))}
          </div>
        );

      case 'location':
        return (
          <button
            type="button"
            className="request-wizard__location-btn"
            onClick={() => setIsMapOpen(true)}
          >
            <MapPin size={16} strokeWidth={1.8} />
            <span>{form.location ? 'ویرایش محدوده انتخاب‌شده' : 'انتخاب روی نقشه'}</span>
          </button>
        );

      default:
        return null;
    }
  };

  const headerText = () => {
    if (stage === STAGE_SEND) return 'نحوه ارسال درخواست';
    if (stage === STAGE_REVIEW) return 'مرور نهایی درخواست';
    return `مرحله ${(stepIndex + 1).toLocaleString('fa-IR')} از ${STEPS.length.toLocaleString('fa-IR')}`;
  };

  const nextButtonText = () => {
    if (stage === STAGE_SEND) return 'ثبت نهایی درخواست';
    if (stage === STAGE_REVIEW) return 'ادامه';
    if (isLastStep) return 'مرور نهایی';
    return 'بعدی';
  };

  return (
    <div className="request-wizard">
      {/* پس‌زمینه کلیک/تاچ را نمی‌بندد؛ فقط دکمه‌ی ضربدر فرم را می‌بندد */}
      <div className="request-wizard__backdrop" />
      <div className="request-wizard__box">
        <div className="request-wizard__header">
          <span className="request-wizard__progress-text">{headerText()}</span>
          <button type="button" className="request-wizard__close" onClick={onClose} aria-label="بستن">
            <X size={18} strokeWidth={1.8} />
          </button>
        </div>

        {stage === STAGE_STEPS && (
          <div className="request-wizard__progress-bar">
            <div
              className="request-wizard__progress-fill"
              style={{ width: `${((stepIndex + 1) / STEPS.length) * 100}%` }}
            />
          </div>
        )}

        <div className="request-wizard__body">
          {stage === STAGE_SEND && (
            <div className="request-wizard__send-options">
              <button
                type="button"
                className={`request-wizard__send-card ${sendMethod === 'free' ? 'is-selected' : ''}`}
                onClick={() => setSendMethod('free')}
              >
                <div className="request-wizard__send-icon">
                  <Zap size={18} strokeWidth={1.8} />
                </div>
                <div className="request-wizard__send-text">
                  <span className="request-wizard__send-title">ارسال رایگان</span>
                  <span className="request-wizard__send-desc">
                    درخواست فقط برای مشاورانی ارسال می‌شود که در منطقه انتخابی شما و داخل اپلیکیشن
                    خانه‌رو فعال هستند — هم‌اکنون حدود{' '}
                    <strong>{freeConsultantsCount.toLocaleString('fa-IR')}</strong> مشاور
                  </span>
                </div>
              </button>

              <button
                type="button"
                className={`request-wizard__send-card ${sendMethod === 'paid' ? 'is-selected' : ''}`}
                onClick={() => setSendMethod('paid')}
              >
                <div className="request-wizard__send-icon">
                  <Send size={18} strokeWidth={1.8} />
                </div>
                <div className="request-wizard__send-text">
                  <span className="request-wizard__send-title">ارسال پیامکی (غیررایگان)</span>
                  <span className="request-wizard__send-desc">
                    درخواست به‌صورت پیامک برای تمام مشاوران املاک منطقه انتخابی ارسال می‌شود،
                    صرف‌نظر از اینکه در اپلیکیشن خانه‌رو عضو باشند یا نه
                  </span>
                </div>
              </button>
            </div>
          )}

          {stage === STAGE_REVIEW && (
            <div className="request-wizard__review">
              {STEPS.map((s, index) => (
                <div key={s.id} className="request-wizard__review-item">
                  <div className="request-wizard__review-item-header">
                    <span className="request-wizard__review-title">{s.title}</span>
                    <button
                      type="button"
                      className="request-wizard__review-edit"
                      onClick={() => handleEditStep(index)}
                      aria-label={`ویرایش ${s.title}`}
                    >
                      <Pencil size={13} strokeWidth={1.8} />
                    </button>
                  </div>
                  <p className="request-wizard__review-value">{getStepSummary(s.id, form)}</p>
                  {notes[s.id] && (
                    <p className="request-wizard__review-note">«{notes[s.id]}»</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {stage === STAGE_STEPS && (
            <>
              <h2 className="request-wizard__title">{step.title}</h2>

              {renderStepContent()}

              {step.hasNote && (
                <div className="request-wizard__note">
                  <span className="request-wizard__mini-label">توضیح اختیاری برای این مرحله</span>
                  {NOTE_SUGGESTIONS[step.id] && (
                    <div className="request-wizard__suggestions">
                      {NOTE_SUGGESTIONS[step.id].map((text) => {
                        const used = (notes[step.id] || '').includes(text);
                        return (
                          <button
                            key={text}
                            type="button"
                            className={`request-wizard__suggestion ${used ? 'is-used' : ''}`}
                            disabled={used}
                            onClick={() => {
                              const prev = notes[step.id] || '';
                              updateNote(step.id, prev.trim() ? `${prev.trim()} ${text}` : text);
                            }}
                          >
                            {text}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <textarea
                    rows={2}
                    placeholder="متن پیشنهادی را بزنید یا خودتان بنویسید"
                    value={notes[step.id] || ''}
                    onChange={(e) => updateNote(step.id, e.target.value)}
                  />
                </div>
              )}

              {step.id === 'location' && form.location && (
                <div className="request-wizard__note">
                  <span className="request-wizard__mini-label">محدوده‌ی انتخاب‌شده</span>
                  <AreaPreview geojson={form.location} />
                </div>
              )}
            </>
          )}
        </div>

        <div className="request-wizard__footer">
          <button type="button" className="request-wizard__back-btn" onClick={goBack}>
            <ChevronRight size={16} strokeWidth={2} />
            <span>{stage === STAGE_STEPS && isFirstStep ? 'انصراف' : 'قبلی'}</span>
          </button>
          <button type="button" className="request-wizard__next-btn" onClick={goNext}>
            <span>{nextButtonText()}</span>
            {stage === STAGE_STEPS && <ChevronLeft size={16} strokeWidth={2} />}
          </button>
        </div>
      </div>

      {isMapOpen && (
        <LocationPicker
          onClose={() => setIsMapOpen(false)}
          onConfirm={(geoJson) => {
            update('location', geoJson);
            setIsMapOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default RequestWizard;